import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { Provider } from 'react-redux';
import { persistor, store } from './store/store';
import { PersistGate } from 'redux-persist/integration/react';
import { ToastContainer } from 'react-toastify';
import 'react-datepicker/dist/react-datepicker.css';
import { theme, AppTheme } from './theme/theme';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { AuthProvider, ProtectedRoute, ErrorBoundary } from './shared/components';
import CustomLoader from './shared/components/ui/CustomLoader';

// Eagerly loaded components (critical path)
import Home from './screens/Home/Home';
import { Login } from './features';

// Lazy loaded components
const TutorDashboard = lazy(() => import('./screens/TutorDashboard/TutorDashboard'));
const CourseEdit = lazy(() => import('./features/dashboards/tutor/components/CourseEdit'));
const MyCourses = lazy(() => import('./screens/MyCourses/MyCourses'));
const StudentSignUp = lazy(() => import('./features/studentOnboarding/components/StudentSignUp'));
const StudentDashboard = lazy(() => import('./features/dashboards/student/components/StudentDashboard'));
const ClassDetails = lazy(() => import('./features/dashboards/student/components/ClassDetails'));
const AllStudentClasses = lazy(() => import('./features/studentClasses/components/AllStudentClasses'));
const MenuScreen = lazy(() => import('./screens/MenuScreen/MenuScreen'));
const Search = lazy(() => import('./screens/Search'));
const ParentDetail = lazy(() => import('./screens/ParentDetail/ParentDetail'));
const TutorOnboarding = lazy(() => import('./features/tutorOnboarding/components/TutorOnboarding'));
const TutorCompletionStep = lazy(() => import('./features/tutorOnboarding/components/TutorCompletionStep'));
const StudentCompletionStep = lazy(() => import('./features/studentOnboarding/components/StudentCompletionStep'));
const TutorProfile = lazy(() => import('./features/profiles/components/TutorProfile'));
const StudentProfile = lazy(() => import('./features/profiles/components/StudentProfile'));
const SupportForm = lazy(() => import('./features/support/components/SupportForm'));
const ValidateOTP = lazy(() => import('./features/auth/components/ValidateOTP'));

const GlobalStyle = createGlobalStyle<{theme: AppTheme}>`
  body {
    margin: 0;
    font-family: ${props => props.theme.fonts.body};
  }
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
    box-shadow: 0 0 0 1000px transparent inset !important;
    -webkit-text-fill-color: ${props => props.theme.colors.textLight} !important;
    caret-color: ${props => props.theme.colors.textLight}; 
    transition: background-color 5000s ease-in-out 0s;
  }
`;

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider theme={theme}>
          <ErrorBoundary level="page">
            <ToastContainer />
            <Router>
              <AuthProvider>
                <Suspense fallback={<CustomLoader visible={true} />}>
                  <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Navigate to="/" replace />} />
                  <Route path="/login/student" element={<Login />} />
                  <Route path="/login/tutor" element={<Login />} />
                  <Route path="/tutor-onboarding" element={<TutorOnboarding />} />
                  <Route path="/tutor-onboarding-complete" element={<TutorCompletionStep />} />
                  <Route path="/student-onboarding-complete" element={<StudentCompletionStep />} />
                  <Route path="/validate-otp" element={<ValidateOTP />} />
                  <Route path="/TutorDashboard" element={<ProtectedRoute allowedUserTypes={['tutor']}><TutorDashboard /></ProtectedRoute>} />
                  <Route path="/my-courses" element={<ProtectedRoute allowedUserTypes={['tutor']}><MyCourses /></ProtectedRoute>} />
                  <Route path="/course-edit/:courseId" element={<ProtectedRoute allowedUserTypes={['tutor']}><CourseEdit /></ProtectedRoute>} />
                  <Route path="/StudentSignUpScreen" element={<StudentSignUp/>} />
                  <Route path="/StudentDashboard" element={<ProtectedRoute allowedUserTypes={['student']}><StudentDashboard/></ProtectedRoute>} />
                  <Route path="/all-classes" element={<ProtectedRoute allowedUserTypes={['student']}><AllStudentClasses /></ProtectedRoute>} />
                  <Route path="/class-details/:classId" element={<ProtectedRoute allowedUserTypes={['student']}><ClassDetails /></ProtectedRoute>} />
                  <Route path="/search" element={<Search />} />
                  <Route path="/ParentDetail" element={<ProtectedRoute><ParentDetail /></ProtectedRoute>} />
                  <Route path="/MenuScreen" element={<ProtectedRoute><MenuScreen /></ProtectedRoute>} />
                  <Route path="/tutor/:tutorId" element={<ProtectedRoute><TutorProfile /></ProtectedRoute>} />
                  <Route path="/student/:studentId" element={<ProtectedRoute><StudentProfile /></ProtectedRoute>} />
                  <Route path="/support" element={<ProtectedRoute><SupportForm /></ProtectedRoute>} />
                  
                  {/* Legacy routes - redirect to correct flow */}
                  <Route path="/AddCourseBasicScreen" element={<Navigate to="/course-edit/new" replace />} />
                  <Route path="/AddCourseContentScreen" element={<Navigate to="/course-edit/new" replace />} />
                </Routes>
                </Suspense>
              </AuthProvider>
            </Router>
          </ErrorBoundary>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
